/**
 * PolygrafConnection — depth seam over the WebSocket transport.
 *
 * Owns connect / reconnect / ping / dispatch. Callers register handlers per
 * message type and send JSON-serializable objects; this module knows nothing
 * about Polygraf message semantics beyond the `type` field.
 */
import { getNativeApiUrl } from './native-bridge.js';
// ============================================================================
// Types
// ============================================================================
/**
 * Emitted via `onError` when the reconnect budget is exhausted. Exported so
 * consumers can classify this terminal case without duplicating the literal
 * (the writer UI keys its re-auth recovery panel off it).
 */
export const RECONNECT_RETRIES_EXHAUSTED = 'Max reconnection attempts reached';
// ============================================================================
// URL resolution
// ============================================================================
/**
 * Resolve the API base URL the browser should hit.
 *
 * Priority: native WebView bridge → `envApiUrl` (Vite injects `VITE_API_URL`
 * at build time, so callers pass it in) → `fallback`.
 *
 * Default `fallback` is empty string, meaning "relative URLs" — appropriate
 * when a dev proxy or same-origin server handles `/api/...`.
 */
export function resolveApiBaseUrl(envApiUrl, fallback = '') {
    const native = getNativeApiUrl();
    if (native)
        return native;
    if (envApiUrl)
        return envApiUrl;
    return fallback;
}
/**
 * Build a Polygraf WebSocket URL from a base API URL.
 *
 * Used by viewers + writers — the API and WS endpoints share an origin and
 * differ only in scheme.
 */
export function buildWsUrlFromApiUrl(apiUrl) {
    const url = new URL(apiUrl);
    const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${url.host}/ws`;
}
/**
 * Resolve the Polygraf WebSocket URL.
 *
 * Priority: native bridge → `envWsUrl` → `envApiUrl`-derived → `fallback` →
 * `window.location`-derived. Throws if no source resolves and there's no
 * `window` (e.g. Node without options).
 */
export function resolveWsUrl(options = {}) {
    const nativeApiUrl = getNativeApiUrl();
    if (nativeApiUrl)
        return buildWsUrlFromApiUrl(nativeApiUrl);
    if (options.envWsUrl)
        return options.envWsUrl;
    if (options.envApiUrl)
        return buildWsUrlFromApiUrl(options.envApiUrl);
    if (options.fallback)
        return options.fallback;
    if (typeof window !== 'undefined' && window.location) {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        return `${protocol}//${window.location.host}/ws`;
    }
    throw new Error('resolveWsUrl: no URL source available. Pass `envWsUrl`, `envApiUrl`, or `fallback`.');
}
// ============================================================================
// PolygrafConnection
// ============================================================================
const DEFAULT_OPTIONS = {
    maxReconnectAttempts: 5,
    reconnectBaseDelay: 1000,
    maxReconnectDelay: 30_000,
    pingInterval: 30_000,
    autoConnect: true,
};
const PING_PAYLOAD = JSON.stringify({ type: 'ping' });
/** Default budget for a {@link PolygrafConnection.ensureAlive} pong. */
export const DEFAULT_LIVENESS_PROBE_MS = 3000;
export class PolygrafConnection {
    events;
    ws = null;
    state = 'disconnected';
    reconnectAttempts = 0;
    reconnectTimer = null;
    pingTimer = null;
    manualClose = false;
    /** Resolvers waiting for the next pong (see {@link ensureAlive}). */
    pongWaiters = new Set();
    handlers = new Map();
    opts;
    constructor(options = {}, events = {}) {
        this.events = events;
        const wsCtor = options.WebSocketImpl ?? globalThis.WebSocket;
        if (!wsCtor) {
            throw new Error('PolygrafConnection: no WebSocket constructor available. Pass WebSocketImpl in options.');
        }
        this.opts = {
            url: options.url,
            maxReconnectAttempts: options.maxReconnectAttempts ?? DEFAULT_OPTIONS.maxReconnectAttempts,
            reconnectBaseDelay: options.reconnectBaseDelay ?? DEFAULT_OPTIONS.reconnectBaseDelay,
            maxReconnectDelay: options.maxReconnectDelay ?? DEFAULT_OPTIONS.maxReconnectDelay,
            pingInterval: options.pingInterval ?? DEFAULT_OPTIONS.pingInterval,
            autoConnect: options.autoConnect ?? DEFAULT_OPTIONS.autoConnect,
            WebSocketImpl: wsCtor,
        };
        if (this.opts.autoConnect) {
            this.connect();
        }
    }
    getState() {
        return this.state;
    }
    /**
     * Register a handler for a message type. Returns an unsubscribe function.
     * Multiple handlers per type are supported and called in registration order.
     */
    on(type, handler) {
        const wrapped = handler;
        const set = this.handlers.get(type) ?? new Set();
        set.add(wrapped);
        this.handlers.set(type, set);
        return () => {
            set.delete(wrapped);
            if (set.size === 0)
                this.handlers.delete(type);
        };
    }
    /** Send a JSON-serializable message. No-op if not connected. */
    send(data) {
        if (this.state !== 'connected' || !this.ws)
            return;
        this.ws.send(JSON.stringify(data));
    }
    /** Connect (or reconnect, from a disconnected state). */
    connect() {
        if (this.state !== 'disconnected')
            return;
        this.state = 'connecting';
        this.manualClose = false;
        this.cancelReconnect();
        const url = this.opts.url ?? resolveWsUrl();
        try {
            this.ws = new this.opts.WebSocketImpl(url);
        }
        catch (err) {
            this.state = 'disconnected';
            this.events.onError?.(err instanceof Error ? err : new Error(String(err)));
            this.scheduleReconnect();
            return;
        }
        this.ws.onopen = () => {
            this.state = 'connected';
            this.reconnectAttempts = 0;
            this.startPing();
            this.events.onOpen?.();
        };
        this.ws.onmessage = (event) => {
            this.dispatch(event.data);
        };
        this.ws.onerror = () => {
            this.events.onError?.(new Error('WebSocket error'));
        };
        this.ws.onclose = () => {
            this.state = 'disconnected';
            this.stopPing();
            this.settlePongWaiters(false);
            this.events.onClose?.();
            if (!this.manualClose) {
                this.scheduleReconnect();
            }
        };
    }
    /** Disconnect cleanly; cancels reconnect attempts. */
    disconnect() {
        this.manualClose = true;
        this.cancelReconnect();
        this.stopPing();
        this.closeCurrentSocket();
        this.state = 'disconnected';
    }
    /** Force a fresh reconnection: resets attempt counter and reconnects. */
    reconnect() {
        this.reconnectAttempts = 0;
        this.cancelReconnect();
        this.stopPing();
        this.closeCurrentSocket();
        this.state = 'disconnected';
        this.connect();
    }
    /**
     * Verify the socket still carries traffic and reconnect only when it does
     * not.
     *
     * A WebSocket can look connected while no data flows — an iOS WebView the
     * host app suspended reports `OPEN` until a long TCP timeout (MP-160).
     * Callers that used to reconnect blindly on such a suspicion now probe
     * first: a reconnect re-subscribes, the server answers a subscribe with the
     * FULL document, and the viewer renders that as the whole transcript
     * reloading and jumping to the end (MU N3, 2026-09-04).
     *
     * Resolves once the connection is known good or a fresh one is opening.
     */
    async ensureAlive(probeTimeoutMs = DEFAULT_LIVENESS_PROBE_MS) {
        if (this.state !== 'connected' || !this.ws) {
            this.reconnect();
            return;
        }
        const alive = await this.probeLiveness(probeTimeoutMs);
        // Re-check the state: the socket may have closed (and auto-reconnected)
        // while the probe was in flight.
        if (!alive && this.state === 'connected') {
            this.reconnect();
        }
    }
    // ============================================================================
    // Internals
    // ============================================================================
    /**
     * Close the socket we own and detach its callbacks first.
     *
     * `close()` reports through `onclose` asynchronously, so a socket we
     * replaced would otherwise fire it after the successor is already open —
     * flipping `state` back to `disconnected`, emitting a spurious `onClose`
     * and scheduling one more connect. Each such connect re-subscribes and
     * makes the server resend the whole document (MU N3, 2026-09-04).
     */
    closeCurrentSocket() {
        const ws = this.ws;
        if (!ws)
            return;
        this.ws = null;
        ws.onopen = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        this.settlePongWaiters(false);
        ws.close();
    }
    /** Resolve every pending liveness probe with `alive`. */
    settlePongWaiters(alive) {
        if (this.pongWaiters.size === 0)
            return;
        const waiters = [...this.pongWaiters];
        this.pongWaiters.clear();
        for (const resolve of waiters)
            resolve(alive);
    }
    /** Send one ping and report whether a pong came back in time. */
    probeLiveness(timeoutMs) {
        const ws = this.ws;
        if (this.state !== 'connected' || !ws)
            return Promise.resolve(false);
        return new Promise((resolve) => {
            const waiter = (alive) => {
                clearTimeout(timer);
                resolve(alive);
            };
            const timer = setTimeout(() => {
                this.pongWaiters.delete(waiter);
                resolve(false);
            }, timeoutMs);
            this.pongWaiters.add(waiter);
            ws.send(PING_PAYLOAD);
        });
    }
    dispatch(raw) {
        let data;
        try {
            data = JSON.parse(typeof raw === 'string' ? raw : String(raw));
        }
        catch {
            return;
        }
        // Pong is internal — never bubbled to handlers. It answers both the
        // keepalive ping and an ensureAlive probe.
        if (data.type === 'pong') {
            this.settlePongWaiters(true);
            return;
        }
        const type = typeof data.type === 'string' ? data.type : '';
        const set = this.handlers.get(type);
        if (set && set.size > 0) {
            for (const h of set)
                h(data);
            return;
        }
        this.events.onUnhandledMessage?.(data);
    }
    startPing() {
        this.stopPing();
        if (this.opts.pingInterval <= 0)
            return;
        this.pingTimer = setInterval(() => {
            if (this.ws && this.state === 'connected') {
                this.ws.send(PING_PAYLOAD);
            }
        }, this.opts.pingInterval);
    }
    stopPing() {
        if (this.pingTimer) {
            clearInterval(this.pingTimer);
            this.pingTimer = null;
        }
    }
    scheduleReconnect() {
        if (this.reconnectAttempts >= this.opts.maxReconnectAttempts) {
            this.events.onError?.(new Error(RECONNECT_RETRIES_EXHAUSTED));
            return;
        }
        this.cancelReconnect();
        const delay = Math.min(this.opts.reconnectBaseDelay * 2 ** this.reconnectAttempts, this.opts.maxReconnectDelay);
        this.reconnectTimer = setTimeout(() => {
            this.reconnectAttempts++;
            this.connect();
        }, delay);
    }
    cancelReconnect() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }
}
//# sourceMappingURL=connection.js.map